import { useEffect, useState } from 'react';

import Link from 'next/link';

import Image from '@/modules/common/components/Image';
import { VisualizerCubePDP } from '@/modules/common/components/icons';
import { Skeleton } from '@/modules/common/components/skeleton';
import {
  SceneByIdQuery,
  SceneFieldsFragment,
  useSceneByIdQuery,
} from '@/modules/product/gql/__generated/sceneAssociation.generated';
import { AddToShoppingListButton } from '@/modules/shopping-list/components/AddToShoppingListButton';
import { ShoppingListSaveType } from '@/modules/shopping-list/hooks/useShoppingListActions';
import { SavedLook } from '@/modules/shopping-list/models/shoppingListTextItem.interface';
import { ExtendedSavedLook } from '@/modules/visualizer/models/extendedSavedLook.types';
import { Surface } from '@/modules/visualizer/services/surfaceTransform';

import { useDmaTasksWithLayerQuery } from '../../gql/__generated/dmaTasks.generated';
import { buildVisualizerUrl } from '../../utils/utils';

export default function SavedLookCard({ look }: { look: SavedLook }) {
  const [loading, setLoading] = useState(false);
  const [scene, setScene] = useState<SceneFieldsFragment | null>(null);
  const [extendedLookData, setExtendedLookData] = useState<ExtendedSavedLook>();

  useEffect(() => {
    const skus = JSON.parse(look.description);
    setLoading(true);

    useSceneByIdQuery
      .fetcher({
        id: parseInt(look.name),
      })()
      .then((res: SceneByIdQuery) => {
        if (!res.dmaScenesCollection?.items?.[0]?.surfaces) {
          setLoading(false);
          return;
        }
        const sceneItem = res.dmaScenesCollection.items[0];
        if (!sceneItem) {
          setLoading(false);
          return;
        }
        setScene(sceneItem);
        const surfaces = (sceneItem.surfaces as Surface[])?.filter((surface) => !!skus[surface.id]);
        Promise.all(
          surfaces.map(async (surface) => {
            const material = await useDmaTasksWithLayerQuery.fetcher({
              sceneId: sceneItem.id,
              surfaceId: surface.id,
              skus: [skus[surface.id]],
              limit: 1,
            })();
            return material?.GetTasksWithLayer?.results?.[0];
          })
        ).then((lookMaterials) => {
          setExtendedLookData({
            ...look,
            backgroundDayUrl: sceneItem.backgroundDayUrl ?? '',
            title: sceneItem.title ?? '',
            surfaces: (sceneItem.surfaces ?? []) as Surface[],
            skus: lookMaterials.filter((material) => !!material),
          });
          setLoading(false);
        });
      });
  }, [look]);

  if (loading) {
    return (
      <div className="flex flex-col">
        <Skeleton className="aspect-square h-auto" />
        <div className="flex min-h-[86px] flex-col gap-1 p-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-5 w-40" />
        </div>
      </div>
    );
  }
  if (!scene || !extendedLookData) {
    return null;
  }

  const visualizerUrl =
    scene.id ?
      buildVisualizerUrl({
        sceneId: scene.id,
        renderScenes: extendedLookData.skus,
      })
    : '';

  return (
    <Link href={visualizerUrl}>
      <div className="relative flex h-full max-h-[80vh] w-full max-w-[100vw] flex-col items-start overflow-hidden object-cover">
        <AddToShoppingListButton
          saveType={ShoppingListSaveType.Look}
          textLineItem={look}
          className="right-2 top-2 z-5 md:right-3 md:top-3"
        />
        <div className="relative w-full overflow-hidden">
          <div className="relative aspect-square w-full transition-transform duration-700 hover:scale-110">
            <Image
              src={extendedLookData.backgroundDayUrl}
              alt={extendedLookData.name}
              className="absolute left-0 top-0 h-full max-h-full w-auto object-cover"
            />
            {extendedLookData.skus.map(
              (sku) =>
                sku && (
                  <Image
                    key={sku.id}
                    alt={sku.title ?? ''}
                    src={sku.image_rendering ?? ''}
                    className="absolute left-0 top-0 h-full max-h-full w-auto object-cover"
                    style={{
                      zIndex: Math.min(sku.layer_level ?? 1, 100),
                    }}
                  />
                )
            )}
          </div>
          <VisualizerCubePDP />
        </div>

        <div className="p-3 text-left">
          <p className="text-xs leading-4 text-gray-500">Configurable room</p>
          <p className="font-bold">{extendedLookData.title || look.name}</p>
        </div>
      </div>
    </Link>
  );
}
